import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDuration, mediaUrl } from "@/lib/format";
import { CategoryThumb } from "@/components/CategoryThumb";
import { VerifiedShield } from "@/components/VerifiedShield";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Calendar as CalIcon, ArrowLeft, CheckCircle2, Repeat } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

function nextDays(n) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    out.push(d);
  }
  return out;
}
const fmt = (d) => d.toISOString().split("T")[0];

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [date, setDate] = useState(fmt(new Date()));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [answers, setAnswers] = useState({});
  const setAns = (fid, v) => setAnswers((a) => ({ ...a, [fid]: v }));
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(null);
  const [secureBalance, setSecureBalance] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.get(`/services/${id}`).then((r) => setService(r.data)).catch(() => setNotFound(true)); }, [id]);
  useEffect(() => {
    setSlot(null);
    api.get(`/services/${id}/slots`, { params: { date } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [id, date]);

  const book = async () => {
    if (!user) { toast.error("Please log in as a customer to book."); navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Only customers can book services."); return; }
    for (const fl of service.intake_fields || []) {
      const v = answers[fl.id];
      if (fl.required && (v === undefined || v === null || String(v).trim() === "")) {
        toast.error(`Please answer: ${fl.prompt}`);
        return;
      }
    }
    setBooking(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        service_id: id, date, start_time: slot, notes, intake_answers: answers, origin_url: window.location.origin,
        secure_balance: secureBalance,
      });
      if (data.free) {
        setDone({ date, start_time: slot });
        toast.success("Booking confirmed!");
      } else if (data.url) {
        window.location.href = data.url; // redirect to Stripe Checkout
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBooking(false);
    }
  };

  const subscribe = async () => {
    if (!user) { toast.error("Please log in as a customer to subscribe."); navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Only customers can subscribe to services."); return; }
    for (const fl of service.intake_fields || []) {
      const v = answers[fl.id];
      if (fl.required && (v === undefined || v === null || String(v).trim() === "")) {
        toast.error(`Please answer: ${fl.prompt}`);
        return;
      }
    }
    setBooking(true);
    try {
      await api.post("/subscriptions/subscribe", {
        service_id: id, start_date: date, start_time: slot, notes, intake_answers: answers,
      });
      toast.success("Subscribed! Manage your visits under My Services.");
      navigate("/my-services");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBooking(false);
    }
  };

  const FREQ_LABEL = { weekly: "weekly", monthly: "monthly", quarterly: "quarterly" };

  if (notFound) return <div className="py-20 text-center text-muted-foreground" data-testid="service-not-found">This service is no longer available.</div>;
  if (!service) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  const isOwner = user && user.id === service.subscriber_id;

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-20 text-center" data-testid="booking-success">
        <CheckCircle2 className="w-16 h-16 text-primary mx-auto" strokeWidth={1.5} />
        <h1 className="font-display text-3xl font-semibold mt-4">Booking confirmed</h1>
        <p className="text-muted-foreground mt-2">{service.title} on {done.date} at {done.start_time}. A confirmation email has been sent.</p>
        <div className="flex gap-3 justify-center mt-8">
          <Link to="/my-bookings"><Button className="rounded-sm" data-testid="go-my-bookings">View my bookings</Button></Link>
          <Link to="/browse"><Button variant="outline" className="rounded-sm">Browse more</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div>
          <div className="border border-border rounded-sm overflow-hidden bg-white">
            <CategoryThumb imageUrl={service.image_url} category={service.category} imgClassName="w-full h-72 object-cover" tileClassName="w-full h-72" testid="service-detail-image" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mt-6">{service.category}</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">{service.title}</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            {service.subscriber_logo && <img src={mediaUrl(service.subscriber_logo)} alt="" className="w-6 h-6 rounded-full object-cover border border-border" />}
            by{" "}
            <Link to={`/p/${service.subscriber_id}`} className="hover:text-primary hover:underline" data-testid="service-provider-link">
              {service.subscriber_name}
            </Link>
            {service.subscriber_verified && <VerifiedShield withLabel testid="service-detail-verified" />}
          </p>
          <p className="mt-5 leading-relaxed text-foreground/80">{service.description || "No description provided."}</p>
          {service.is_recurring && (
            <div className="mt-5 rounded-sm border border-primary/30 bg-primary/5 p-4" data-testid="recurring-callout">
              <p className="text-sm font-semibold text-primary flex items-center gap-1.5"><Repeat className="w-4 h-4" />Recurring service — repeats {FREQ_LABEL[service.recurrence]}</p>
              <p className="text-sm text-foreground/80 mt-1.5">
                This is an ongoing {FREQ_LABEL[service.recurrence]} service. Subscribe once, then pay per visit. You can pause any upcoming visit up to {service.pause_notice_days} day(s) in advance, and cancel anytime.
              </p>
            </div>
          )}

          <h2 className="font-display text-2xl font-semibold mt-10">{service.is_recurring ? "Choose your first visit date" : "Choose a date"}</h2>
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {nextDays(14).map((d) => {
              const val = fmt(d);
              return (
                <button key={val} onClick={() => setDate(val)} data-testid={`date-${val}`}
                  className={`shrink-0 w-16 py-3 border rounded-sm text-center transition-all ${date === val ? "bg-secondary text-white border-secondary" : "border-border hover:border-secondary"}`}>
                  <div className="text-xs uppercase">{d.toLocaleDateString("en", { weekday: "short" })}</div>
                  <div className="font-display text-lg font-semibold">{d.getDate()}</div>
                </button>
              );
            })}
          </div>

          <h2 className="font-display text-2xl font-semibold mt-8">Available slots</h2>
          {slots.length === 0 ? (
            <p className="text-muted-foreground mt-3" data-testid="no-slots">No availability on this day.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4">
              {slots.map((s) => (
                <button key={s.time} disabled={!s.available} onClick={() => setSlot(s.time)} data-testid={`slot-${s.time}`}
                  className={`py-2.5 border rounded-sm text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed
                    ${slot === s.time ? "bg-primary text-white border-primary" : "border-border hover:border-secondary bg-muted"}`}>
                  {s.time}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sticky summary */}
        <div className="lg:sticky lg:top-24 h-fit border border-border rounded-sm bg-white p-6 hard-shadow">
          <h3 className="font-display text-xl font-semibold">Booking summary</h3>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span className="font-medium text-right">{service.title}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><Clock className="w-4 h-4" />Duration</span><span>{formatDuration(service.duration_minutes)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><CalIcon className="w-4 h-4" />Date</span><span>{date}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Time</span><span data-testid="summary-time">{slot || "—"}</span></div>
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            {(service.intake_fields || []).length > 0 && (
              <div className="space-y-4 mb-4" data-testid="intake-form">
                <p className="text-sm font-medium">A few questions from the provider</p>
                {(service.intake_fields || []).map((fl) => (
                  <div key={fl.id} className="space-y-1.5" data-testid={`intake-q-${fl.id}`}>
                    <Label className="text-sm text-foreground/80">
                      {fl.prompt}{fl.required && <span className="text-primary"> *</span>}
                    </Label>
                    {fl.type === "long_text" ? (
                      <Textarea value={answers[fl.id] || ""} onChange={(e) => setAns(fl.id, e.target.value)} className="rounded-sm" rows={3} data-testid={`intake-input-${fl.id}`} />
                    ) : fl.type === "number" ? (
                      <Input type="number" value={answers[fl.id] || ""} onChange={(e) => setAns(fl.id, e.target.value)} className="rounded-sm" data-testid={`intake-input-${fl.id}`} />
                    ) : fl.type === "yes_no" ? (
                      <Select value={answers[fl.id] || ""} onValueChange={(v) => setAns(fl.id, v)}>
                        <SelectTrigger className="rounded-sm" data-testid={`intake-input-${fl.id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : fl.type === "dropdown" ? (
                      <Select value={answers[fl.id] || ""} onValueChange={(v) => setAns(fl.id, v)}>
                        <SelectTrigger className="rounded-sm" data-testid={`intake-input-${fl.id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          {(fl.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={answers[fl.id] || ""} onChange={(e) => setAns(fl.id, e.target.value)} className="rounded-sm" data-testid={`intake-input-${fl.id}`} />
                    )}
                  </div>
                ))}
              </div>
            )}
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-sm" data-testid="booking-notes" />
          </div>
          {service.service_type === "service_call" && (
            <div className="mt-5 rounded-sm border border-primary/30 bg-primary/5 p-3" data-testid="service-call-callout">
              <p className="text-sm font-semibold text-primary">Service call</p>
              <p className="text-xs text-muted-foreground mt-1">
                A <b>non-refundable deposit</b> is charged now to secure your booking. Once on-site, your provider assesses the full cost including any <b>parts &amp; labor</b>, and you pay the remaining balance <b>directly to your provider</b>.
              </p>
            </div>
          )}
          <div className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{service.service_type === "service_call" ? "Estimated total (from)" : service.is_recurring ? "Per visit" : "Service total"}</span>
              <span className="font-display text-xl font-semibold">${service.price}{service.is_recurring ? <span className="text-sm font-normal text-muted-foreground">/{FREQ_LABEL[service.recurrence]?.slice(0,-2) || "visit"}</span> : ""}</span>
            </div>
            {!service.is_recurring && service.deposit > 0 && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Deposit due now</span>
                  <span className="font-display text-2xl font-semibold text-primary" data-testid="deposit-amount">${service.deposit}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Balance paid to provider</span>
                  <span data-testid="balance-amount">${(service.price - service.deposit).toFixed(2)}{service.service_type === "service_call" ? "+" : ""}</span>
                </div>
              </>
            )}
          </div>
          {!service.is_recurring && service.deposit > 0 && (
            <div className="mt-3 rounded-sm border border-amber-300 bg-amber-50 p-3" data-testid="deposit-note">
              <p className="text-xs text-amber-900">You pay a <b>non-refundable ${service.deposit} deposit</b> now to secure your booking. The remaining balance{service.service_type === "service_call" ? " (plus any parts & labor)" : ""} is paid <b>directly to your provider</b> at the time of service.</p>
            </div>
          )}
          {!service.is_recurring && service.deposit > 0 && (service.payment_methods || []).includes("stripe") && (
            <label className="mt-3 flex items-start gap-2.5 rounded-sm border border-primary/30 bg-primary/5 p-3 cursor-pointer" data-testid="secure-balance-toggle">
              <Checkbox checked={secureBalance} onCheckedChange={(v) => setSecureBalance(!!v)} className="mt-0.5" data-testid="secure-balance-checkbox" />
              <span className="text-xs text-muted-foreground">
                <b className="text-foreground">Secure balance with card (optional).</b> Save a card now so your provider can charge the remaining balance at check-in — no need to handle payment on-site. You'll be asked to add your card after paying the deposit.
              </span>
            </label>
          )}
          {service.is_recurring ? (
            <>
              <Button onClick={subscribe} disabled={!slot || booking || isOwner} className="w-full mt-5 rounded-sm" data-testid="subscribe-btn">
                {isOwner ? "This is your service" : booking ? "Processing…" : slot ? "Subscribe to recurring service" : "Select a slot"}
              </Button>
              {isOwner ? (
                <p className="text-xs text-muted-foreground text-center mt-3" data-testid="owner-booking-note">You can't book your own service. This is how customers see it.</p>
              ) : (
                <p className="text-xs text-muted-foreground text-center mt-3">Free to subscribe. You'll pay ${service.price} per visit as each date comes up, and can pause or cancel anytime.</p>
              )}
            </>
          ) : (
            <>
              <Button onClick={book} disabled={!slot || booking || isOwner} className="w-full mt-5 rounded-sm" data-testid="confirm-booking-btn">
                {isOwner ? "This is your service" : booking ? "Processing…" : slot ? `Book & pay $${service.deposit} deposit` : "Select a slot"}
              </Button>
              {isOwner ? (
                <p className="text-xs text-muted-foreground text-center mt-3" data-testid="owner-booking-note">You can't book your own service. This is how customers see it.</p>
              ) : (
                <p className="text-xs text-muted-foreground text-center mt-3">Secure Stripe checkout for your deposit · test mode (use card 4242 4242 4242 4242).</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
